import { useFormContext, Controller } from 'react-hook-form'
import orderBy from 'lodash/orderBy'
import { Spacer, RadioButtonGroup, RadioButton, Input, Select } from '@cb/apricot-react'
import { CBInputMethod } from '@cb/apricot'
import { PROVINCE_OPTIONS, STATES, COUNTRIES } from '@myap/metadata'
import {
  FIELD_DOMESTIC_INTL,
  FIELD_STREET1,
  FIELD_STREET2,
  FIELD_STREET3,
  FIELD_CITY,
  FIELD_STATE,
  FIELD_PROVINCE,
  FIELD_POSTALCODE,
  FIELD_COUNTRY,
  VALUE_DOMESTIC_COUNTRY,
  VALUE_DOMESTIC,
  VALUE_INTERNATIONAL,
} from '../constants'
import {
  validateAddressLine,
  validateCity,
  validatePostalCode,
  validateFieldState,
} from '../validations'

const states = orderBy(
  STATES.filter(state => state.displayInd === 'Y').map(({ code, descr }) => ({
    value: code,
    label: descr,
  })),
  ['label'],
  ['asc']
)
const countries = orderBy(
  COUNTRIES.map(({ iso, countryName }) => ({
    value: iso.toUpperCase(),
    label: countryName,
  })).filter(({ value }) => value !== VALUE_DOMESTIC_COUNTRY),
  ['label'],
  ['asc']
)

function HomeAddress({ disabled }) {
  const { control, setValue, watch } = useFormContext()
  const domesticOrIntVal = watch(FIELD_DOMESTIC_INTL)
  const countryVal = watch(FIELD_COUNTRY)
  const isDomesticAddress = domesticOrIntVal === VALUE_DOMESTIC
  const isGBOrCA = countryVal === 'GB' || countryVal === 'CA'

  useEffect(() => {
    CBInputMethod()
  }, [])

  return (
    <>
      <Controller
        control={control}
        name={FIELD_DOMESTIC_INTL}
        disabled={disabled}
        render={({ field: { onChange, onBlur, value, disabled, name } }) => (
          <RadioButtonGroup
            name={name}
            legend="Address"
            required
            vertical={true}
            onChange={onChange}
            value={value}
          >
            <RadioButton
              name={name}
              label="U.S., U.S. Territories, and Puerto Rico"
              value={VALUE_DOMESTIC}
              disabled={disabled}
              onChange={value => {
                onChange(value)
                setValue(FIELD_STREET3, '')
                setValue(FIELD_CITY, '')
                setValue(FIELD_POSTALCODE, '')
                setValue(FIELD_COUNTRY, VALUE_DOMESTIC_COUNTRY)
                setValue(FIELD_PROVINCE, '')
              }}
              onBlur={value => onBlur(value)}
            />
            <RadioButton
              name={name}
              label="Outside the U.S."
              value={VALUE_INTERNATIONAL}
              disabled={disabled}
              onChange={value => {
                onChange(value)
                setValue(FIELD_CITY, '')
                setValue(FIELD_POSTALCODE, '')
                setValue(FIELD_STATE, '')
                setValue(FIELD_COUNTRY, '')
              }}
              onBlur={value => onBlur(value)}
            />
          </RadioButtonGroup>
        )}
      />
      <Spacer size="24" />
      {!isDomesticAddress ? (
        <>
          <Controller
            control={control}
            name={FIELD_COUNTRY}
            disabled={disabled}
            rules={{
              required: 'Country is required',
            }}
            render={({
              field: { onChange, onBlur, value, disabled, name },
              fieldState: { invalid, isDirty, isTouched, error },
            }) => (
              <Select
                name={name}
                label="Country"
                required
                disabled={disabled}
                onChange={value => onChange(value)}
                onBlur={value => onBlur(value)}
                value={value}
                values={countries}
                validation={validateFieldState({
                  invalid,
                  isDirty,
                  isTouched,
                  error,
                  value,
                })}
                validationMsg={error?.message}
              />
            )}
          />
          <Spacer size="24" />
        </>
      ) : null}
      <Controller
        control={control}
        name={FIELD_STREET1}
        disabled={disabled}
        rules={{
          required: 'Address line 1 is required',
          validate: value => validateAddressLine(value),
        }}
        render={({
          field: { onChange, onBlur, value, disabled, name },
          fieldState: { invalid, isDirty, isTouched, error },
        }) => (
          <Input
            label="Address line 1"
            name={name}
            value={value}
            onChange={value => onChange(value)}
            onBlur={value => onBlur(value)}
            required
            clearable={false}
            floating={false}
            autoComplete="off"
            maxLength="50"
            disabled={disabled}
            validation={validateFieldState({ invalid, isDirty, isTouched, error, value })}
            validationMsg={error?.message}
          />
        )}
      />
      <Spacer size="24" />
      <Controller
        control={control}
        name={FIELD_STREET2}
        disabled={disabled}
        rules={{
          validate: value => validateAddressLine(value),
        }}
        render={({
          field: { onChange, onBlur, value, disabled, name },
          fieldState: { invalid, isDirty, isTouched, error },
        }) => (
          <Input
            label="Address line 2"
            name={name}
            value={value}
            onChange={value => onChange(value)}
            onBlur={value => onBlur(value)}
            clearable={false}
            floating={false}
            autoComplete="off"
            maxLength="50"
            disabled={disabled}
            validation={validateFieldState({ invalid, isDirty, isTouched, error, value })}
            validationMsg={error?.message}
          />
        )}
      />
      <Spacer size="24" />
      {!isDomesticAddress ? (
        <>
          <Controller
            control={control}
            name={FIELD_STREET3}
            disabled={disabled}
            rules={{
              validate: value => validateAddressLine(value),
            }}
            render={({
              field: { onChange, onBlur, value, disabled, name },
              fieldState: { invalid, isDirty, isTouched, error },
            }) => (
              <Input
                label="Address line 3"
                name={name}
                value={value}
                onChange={value => onChange(value)}
                onBlur={value => onBlur(value)}
                clearable={false}
                floating={false}
                autoComplete="off"
                maxLength="50"
                disabled={disabled}
                validation={validateFieldState({
                  invalid,
                  isDirty,
                  isTouched,
                  error,
                  value,
                })}
                validationMsg={error?.message}
              />
            )}
          />
          <Spacer size="24" />
        </>
      ) : null}
      <Controller
        control={control}
        name={FIELD_CITY}
        disabled={disabled}
        rules={{
          required: 'City is required',
          validate: value => validateCity(value),
        }}
        render={({
          field: { onChange, onBlur, value, disabled, name },
          fieldState: { invalid, isDirty, isTouched, error },
        }) => (
          <Input
            label="City"
            name={name}
            value={value}
            onChange={value => onChange(value)}
            onBlur={value => onBlur(value)}
            required
            clearable={false}
            floating={false}
            autoComplete="off"
            maxLength="50"
            disabled={disabled}
            validation={validateFieldState({ invalid, isDirty, isTouched, error, value })}
            validationMsg={error?.message}
          />
        )}
      />
      <Spacer size="24" />
      {isDomesticAddress ? (
        <Controller
          control={control}
          name={FIELD_STATE}
          disabled={disabled}
          rules={{
            required: 'State is required',
          }}
          render={({
            field: { onChange, onBlur, value, disabled, name },
            fieldState: { invalid, isDirty, isTouched, error },
          }) => (
            <>
              <Select
                name={name}
                label="State"
                required
                disabled={disabled}
                onChange={value => onChange(value)}
                onBlur={value => onBlur(value)}
                value={value}
                values={states}
                validation={validateFieldState({
                  invalid,
                  isDirty,
                  isTouched,
                  error,
                  value,
                })}
                validationMsg={error?.message}
              />
              <Spacer size="24" />
            </>
          )}
        />
      ) : null}
      <Controller
        control={control}
        name={FIELD_POSTALCODE}
        disabled={disabled}
        rules={{
          required: `${isDomesticAddress ? 'ZIP' : 'Postal'} code is required`,
          validate: value => validatePostalCode(isDomesticAddress)(value),
        }}
        render={({
          field: { onChange, onBlur, value, disabled, name },
          fieldState: { invalid, isDirty, isTouched, error },
        }) => (
          <Input
            label={`${isDomesticAddress ? 'ZIP' : 'Postal'} code`}
            name={name}
            value={value}
            onChange={value => onChange(value)}
            onBlur={value => onBlur(value)}
            required
            clearable={false}
            floating={false}
            autoComplete="off"
            maxLength={isDomesticAddress ? '5' : '10'}
            disabled={disabled}
            validation={validateFieldState({ invalid, isDirty, isTouched, error, value })}
            validationMsg={error?.message}
          />
        )}
      />
      <Spacer size="24" />
      {!isDomesticAddress ? (
        isGBOrCA ? (
          <Controller
            control={control}
            name={FIELD_PROVINCE}
            disabled={disabled}
            rules={{
              required: 'Province is required',
            }}
            render={({
              field: { onChange, onBlur, value, disabled, name },
              fieldState: { invalid, isDirty, isTouched, error },
            }) => (
              <Select
                name={name}
                label="Province"
                required
                disabled={disabled}
                onChange={value => onChange(value)}
                onBlur={value => onBlur(value)}
                value={value}
                values={PROVINCE_OPTIONS[countryVal]}
                labelNoValue
                validation={validateFieldState({
                  invalid,
                  isDirty,
                  isTouched,
                  error,
                  value,
                })}
                validationMsg={error?.message}
              />
            )}
          />
        ) : (
          <Controller
            control={control}
            name={FIELD_PROVINCE}
            disabled={disabled}
            rules={{
              required: 'Region/Province is required',
            }}
            render={({
              field: { onChange, onBlur, value, disabled, name },
              fieldState: { invalid, isDirty, isTouched, error },
            }) => (
              <Input
                label="Region/Province"
                name={name}
                value={value}
                onChange={value => onChange(value)}
                onBlur={value => onBlur(value)}
                required
                clearable={false}
                floating={false}
                autoComplete="off"
                maxLength="32"
                disabled={disabled}
                validation={validateFieldState({
                  invalid,
                  isDirty,
                  isTouched,
                  error,
                  value,
                })}
                validationMsg={error?.message}
              />
            )}
          />
        )
      ) : null}
      <Spacer size="24" />
    </>
  )
}

export default HomeAddress
